import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePackageTypesList } from '@/features/masters/package-types/usePackageTypes';
import { useWarehousesList } from '@/features/masters/warehouses/useWarehouses';
import { listMaster, type MasterListResult } from '@/lib/api-masters';
import type { IncomingItem } from '@/features/masters/incoming-items/types';
import { EVIDENCE_MEDIA_ACCEPT, uploadEvidence } from '@/features/evidence/api';
import type { ApiClientError } from '@/lib/api-client';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  FileSourceChooser,
  fileMatchesEvidenceMediaAccept,
} from '@/features/file-library';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { LocalizedApiError, localizeApiError } from '@/features/audit-issues';

import {
  inboundPackageCompleteSchema,
  type InboundPackageCompleteInput,
  type InboundPackageDto,
} from './types';
import { useBatchFacilityId, useCompleteInboundPackage } from './useInbound';
import { attachInboundEvidenceFromLibrary } from './api';

const NS = 'operations';
const INCOMING_ITEMS_PAGE_SIZE = 200;

const parseKg = (raw: string): number => {
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const formatKg = (n: number): string => (Number.isFinite(n) ? n.toFixed(1) : '0.0');
const formatDecimalInput = (value: string | null | undefined): string => {
  if (!value) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n
    .toFixed(3)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '.0');
};

interface PhotoThumbProps {
  label: string;
  onRemove: () => void;
  removeLabel: string;
}

const PhotoThumb = ({ label, onRemove, removeLabel }: PhotoThumbProps) => (
  <div className="group relative inline-flex items-center gap-1 rounded-md border border-efs-border bg-efs-bg-subtle px-2 py-1 text-xs">
    <CheckCircle2 className="h-3.5 w-3.5 text-green-900" />
    <span className="max-w-[10rem] truncate">{label}</span>
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="ml-1 rounded p-0.5 text-efs-text-muted hover:bg-efs-bg-panel hover:text-red-900"
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

interface InboundPackageEditDialogProps {
  batchId: string;
  pkg: InboundPackageDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export const InboundPackageEditDialog = ({
  batchId,
  pkg,
  open,
  onOpenChange,
}: InboundPackageEditDialogProps) => {
  const { t } = useTranslation([NS, 'issues']);
  const currentUser = useCurrentUser();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();
  const complete = useCompleteInboundPackage();

  const packageTypesQuery = usePackageTypesList({ active: true, per_page: 200 });
  const packageTypes = useMemo(() => packageTypesQuery.data?.rows ?? [], [packageTypesQuery.data]);

  const itemsQuery = useInfiniteQuery<
    MasterListResult<IncomingItem>,
    ApiClientError,
    InfiniteData<MasterListResult<IncomingItem>, number>,
    readonly unknown[],
    number
  >({
    queryKey: [
      'incoming-items',
      'package-edit-all',
      { status: 'approved', active: true, per_page: INCOMING_ITEMS_PAGE_SIZE },
    ],
    enabled: open,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listMaster<IncomingItem>('incoming-items', {
        status: 'approved',
        active: true,
        page: pageParam,
        per_page: INCOMING_ITEMS_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.rows.length, 0);
      const nextPage = (lastPage.meta.page || allPages.length) + 1;

      if (loaded >= lastPage.meta.total || lastPage.rows.length === 0) return undefined;
      return nextPage;
    },
  });
  const {
    data: incomingItemPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isSuccess: incomingItemsQuerySucceeded,
  } = itemsQuery;
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [open, incomingItemPages?.pages.length, fetchNextPage, hasNextPage, isFetchingNextPage]);
  const loadedIncomingItems = useMemo(() => {
    const seen = new Map<string, IncomingItem>();
    for (const page of incomingItemPages?.pages ?? []) {
      for (const item of page.rows) {
        if (!seen.has(item.incoming_item_id)) seen.set(item.incoming_item_id, item);
      }
    }
    return Array.from(seen.values());
  }, [incomingItemPages?.pages]);
  const incomingItemsLoaded = incomingItemsQuerySucceeded && !hasNextPage && !isFetchingNextPage;
  const incomingItems = useMemo(() => {
    const rows = loadedIncomingItems;
    if (!pkg || rows.some((it) => it.incoming_item_id === pkg.incoming_item_id)) return rows;
    return [
      {
        incoming_item_id: pkg.incoming_item_id,
        japanese_name: pkg.raw_incoming_item_name,
        normalized_name: pkg.raw_incoming_item_name,
        category_group: 'other' as const,
        active_flag: false,
        effective_date: '',
        approval_status: 'retired' as const,
        approved_by: null,
        approved_at: null,
        default_route_type: null,
      },
      ...rows,
    ];
  }, [loadedIncomingItems, pkg]);

  const batchFacilityId = useBatchFacilityId(batchId);
  const warehousesQuery = useWarehousesList(
    { active_only: true, per_page: 200, facility_id: batchFacilityId },
    { enabled: !!batchFacilityId },
  );
  const warehouses = warehousesQuery.data?.rows ?? [];

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [photoSourceMode, setPhotoSourceMode] = useState<FileSourceMode>('local');
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);

  const initialDefaults = (): InboundPackageCompleteInput => ({
    incoming_item_id: pkg?.incoming_item_id ?? '',
    package_type_id: pkg?.package_type_id ?? '',
    warehouse_id: pkg?.warehouse_id ?? '',
    gross_kg: formatDecimalInput(pkg?.gross_kg),
    tare_kg: formatDecimalInput(pkg?.tare_kg),
    photo_evidence_file_ids: pkg?.photo_evidence_file_ids ?? [],
    notes: pkg?.notes ?? '',
    change_reason: '',
  });

  const isReEdit = pkg?.status === 'completed';

  const form = useForm<InboundPackageCompleteInput>({
    resolver: zodResolver(inboundPackageCompleteSchema),
    defaultValues: initialDefaults(),
  });

  // Reset when the dialog opens for a different package.
  useEffect(() => {
    if (open) {
      form.reset(initialDefaults());
      setUploadError(null);
      complete.reset();
      setPhotoSourceMode('local');
      setLibraryPickerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pkg?.inbound_package_id]);

  const watchedPackageType = form.watch('package_type_id');
  const watchedIncomingItem = form.watch('incoming_item_id');
  const grossRaw = form.watch('gross_kg');
  const tareRaw = form.watch('tare_kg');
  const photoIds = form.watch('photo_evidence_file_ids') ?? [];
  const itemChanged = Boolean(
    pkg && watchedIncomingItem && watchedIncomingItem !== pkg.incoming_item_id,
  );
  const selectedIncomingItemName =
    incomingItems.find((it) => it.incoming_item_id === watchedIncomingItem)?.japanese_name ??
    watchedIncomingItem;

  const netKg = useMemo(
    () => Math.max(0, parseKg(grossRaw) - parseKg(tareRaw)),
    [grossRaw, tareRaw],
  );
  const canUseFileLibrary =
    fileLibraryEnabled &&
    (hasPermission(currentUser, 'file_library.read') ||
      hasPermission(currentUser, 'file_library.manage'));
  const canViewTechnicalErrors = hasPermission(currentUser, 'system.admin');
  const localizedMutationError = (
    error: unknown,
    fallbackKey: string,
    defaultValue: string,
  ): string => {
    const text = localizeApiError((key, opts) => t(key, { ns: 'issues', ...(opts ?? {}) }), error);
    return text.cataloged ? text.message : t(fallbackKey, { defaultValue });
  };

  // Auto-fill default tare when a package type is picked and tare is empty.
  useEffect(() => {
    if (!watchedPackageType) return;
    const pt = packageTypes.find((p) => p.package_type_id === watchedPackageType);
    if (!pt) return;
    const currentTare = form.getValues('tare_kg');
    if (!currentTare || currentTare === '0' || currentTare === '0.000') {
      form.setValue('tare_kg', formatDecimalInput(pt.default_tare_kg), { shouldValidate: false });
    }
  }, [watchedPackageType, packageTypes, form]);

  const handlePhotos = async (files: FileList) => {
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        const ev = await uploadEvidence(batchId, file, 'photo');
        uploaded.push(ev.evidence_file_id);
      }
      const current = form.getValues('photo_evidence_file_ids') ?? [];
      form.setValue('photo_evidence_file_ids', [...current, ...uploaded], {
        shouldValidate: true,
      });
    } catch (err) {
      setUploadError(
        localizedMutationError(
          err,
          'efs.operations.inbound.errors.package_photo_upload_failed',
          '包装写真を追加できませんでした。時間をおいて再試行してください。',
        ),
      );
    } finally {
      setUploading(false);
    }
  };

  const evidenceTypeForLibraryFile = (file: FileLibrarySelection) =>
    file.detected_mime_type?.startsWith('image/') ? 'photo' : 'other';

  const handleLibraryPhoto = async (file: FileLibrarySelection) => {
    setUploading(true);
    setUploadError(null);
    try {
      const ev = await attachInboundEvidenceFromLibrary(
        batchId,
        file.company_file_id,
        evidenceTypeForLibraryFile(file),
      );
      const current = form.getValues('photo_evidence_file_ids') ?? [];
      form.setValue('photo_evidence_file_ids', [...current, ev.evidence_file_id], {
        shouldValidate: true,
      });
    } catch (err) {
      setUploadError(
        localizedMutationError(
          err,
          'efs.operations.inbound.errors.package_photo_attach_failed',
          'ファイル庫の包装写真を追加できませんでした。時間をおいて再試行してください。',
        ),
      );
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (fileId: string) => {
    const current = form.getValues('photo_evidence_file_ids') ?? [];
    form.setValue(
      'photo_evidence_file_ids',
      current.filter((id) => id !== fileId),
      { shouldValidate: true },
    );
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (!pkg) return;
    const reason = values.change_reason?.trim() ?? '';
    if ((isReEdit || itemChanged) && !reason) {
      form.setError('change_reason', {
        type: 'required',
        message: t('efs.operations.inbound.package_edit.change_reason_required', {
          defaultValue: '修改原因は必須です',
        }),
      });
      return;
    }
    const body: InboundPackageCompleteInput = {
      incoming_item_id: values.incoming_item_id,
      package_type_id: values.package_type_id,
      warehouse_id: values.warehouse_id.trim(),
      gross_kg: values.gross_kg,
      tare_kg: values.tare_kg,
      photo_evidence_file_ids: values.photo_evidence_file_ids,
      notes: values.notes?.trim() ? values.notes.trim() : undefined,
      change_reason: reason || undefined,
    };
    await complete.mutateAsync({ packageId: pkg.inbound_package_id, body });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('efs.operations.inbound.package_edit.title')}</DialogTitle>
          {pkg ? (
            <DialogDescription>
              <span className="font-mono text-xs">
                {pkg.package_code} — {pkg.raw_incoming_item_name}
              </span>
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="incoming_item_id"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('efs.operations.inbound.package_edit.incoming_item')}</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger disabled={!incomingItemsLoaded}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {incomingItems.map((it) => (
                            <SelectItem key={it.incoming_item_id} value={it.incoming_item_id}>
                              {it.japanese_name}
                              {!it.active_flag || it.approval_status !== 'approved'
                                ? ` (${t('efs.operations.inbound.package_edit.current_item_only')})`
                                : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <p className="text-xs text-efs-text-muted">
                      {incomingItemsLoaded
                        ? t('efs.operations.inbound.package_edit.incoming_item_hint')
                        : t('efs.operations.inbound.package_edit.incoming_item_loading')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {itemChanged ? (
                <div className="sm:col-span-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {t('efs.operations.inbound.package_edit.incoming_item_impact', {
                        before: pkg?.raw_incoming_item_name ?? '—',
                        after: selectedIncomingItemName ?? '—',
                      })}
                    </span>
                  </div>
                </div>
              ) : null}
              <FormField
                control={form.control}
                name="package_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.inbound.package_edit.package_type')}</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {packageTypes.map((pt) => (
                            <SelectItem key={pt.package_type_id} value={pt.package_type_id}>
                              {pt.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="warehouse_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.inbound.package_edit.warehouse')}</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('efs.operations.inbound.package.warehouse_placeholder')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map((wh) => (
                            <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                              {wh.code} — {wh.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gross_kg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.inbound.package_edit.gross_kg')}</FormLabel>
                    <FormControl>
                      <Input type="text" inputMode="decimal" placeholder="0.0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tare_kg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.inbound.package_edit.tare_kg')}</FormLabel>
                    <FormControl>
                      <Input type="text" inputMode="decimal" placeholder="0.0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="sm:col-span-2">
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.package_edit.net_kg')}:{' '}
                  <span className="font-mono text-efs-text-primary">{formatKg(netKg)}</span>
                </p>
              </div>

              <FormField
                control={form.control}
                name="photo_evidence_file_ids"
                render={() => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('efs.operations.inbound.package_edit.photos')}</FormLabel>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={fileRef}
                        type="file"
                        accept={EVIDENCE_MEDIA_ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            void handlePhotos(e.target.files);
                          }
                          e.target.value = '';
                        }}
                      />
                      {canUseFileLibrary ? (
                        <FileSourceChooser
                          value={photoSourceMode}
                          onChange={setPhotoSourceMode}
                          disabled={uploading}
                        />
                      ) : null}
                      {photoSourceMode === 'library' && canUseFileLibrary ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setLibraryPickerOpen(true)}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="mr-1 h-4 w-4" />
                          )}
                          {t('efs.file_library.picker.title', {
                            ns: 'fileLibrary',
                            defaultValue: 'ファイル庫から選択',
                          })}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="mr-1 h-4 w-4" />
                          )}
                          {t('efs.operations.inbound.actions.upload_package_photo')}
                        </Button>
                      )}
                      {photoIds.map((fid, index) => (
                        <PhotoThumb
                          key={fid}
                          label={t('efs.operations.inbound.package_edit.photo_label', {
                            index: index + 1,
                            defaultValue: '写真 {{index}}',
                          })}
                          onRemove={() => removePhoto(fid)}
                          removeLabel={t('efs.operations.inbound.truck_photos.remove')}
                        />
                      ))}
                    </div>
                    {uploadError ? <p className="text-xs text-red-900">{uploadError}</p> : null}
                    <FileLibraryPickerDialog
                      open={libraryPickerOpen}
                      onOpenChange={setLibraryPickerOpen}
                      batchId={batchId}
                      acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
                      acceptFile={fileMatchesEvidenceMediaAccept}
                      onSelect={(file) => {
                        void handleLibraryPhoto(file);
                      }}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('efs.operations.inbound.package_edit.notes')}</FormLabel>
                    <FormControl>
                      <Textarea rows={2} {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="change_reason"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      {t('efs.operations.inbound.package_edit.change_reason', {
                        defaultValue: '修改原因',
                      })}
                      {isReEdit || itemChanged ? (
                        <span className="ml-1 text-red-900">*</span>
                      ) : (
                        <span className="ml-1 text-xs text-efs-text-muted">
                          (
                          {t('efs.operations.inbound.package_edit.change_reason_optional', {
                            defaultValue: '任意',
                          })}
                          )
                        </span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder={t(
                          'efs.operations.inbound.package_edit.change_reason_placeholder',
                          {
                            defaultValue: '例: 重量を測り直したため修正',
                          },
                        )}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    {isReEdit || itemChanged ? (
                      <p className="text-xs text-efs-text-muted">
                        {t('efs.operations.inbound.package_edit.change_reason_hint')}
                      </p>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {complete.error ? (
              <LocalizedApiError
                error={complete.error}
                showTechnicalDetails={canViewTechnicalErrors}
                className="text-xs"
              />
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={complete.isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={complete.isPending}>
                {complete.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.operations.inbound.package_edit.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InboundPackageEditDialog;
